import { MouseEvent, ReactElement, cloneElement } from 'react';
import { MenuTriggerContext, useMenuContext } from './MenuContext';

/**
 * Menu.Trigger
 *
 * Wraps a single child element and wires it up as the menu toggle. Injects the
 * CSS `anchorName` so `Menu.Content` can position itself relative to it.
 *
 * Use this when you need a custom trigger element (e.g. an icon button). For a
 * standard styled button trigger, prefer `Menu.Button` instead.
 */

interface MenuTriggerProps {
  children: ReactElement; // only accepts a single child
}

export function MenuTrigger({ children }: MenuTriggerProps) {
  const { anchorName, toggle, isOpen, popoverId } = useMenuContext();

  return (
    <MenuTriggerContext.Provider value={true}>
      {cloneElement(children, {
        style: { ...children.props.style, anchorName },
        popovertarget: popoverId,
        popovertargetaction: 'show',
        onClick: async (e: MouseEvent) => {
          await children.props.onClick?.(e);
          if (e.defaultPrevented) {
            return;
          }
          toggle();
        },
        'aria-haspopup': 'menu',
        'aria-expanded': isOpen || undefined,
      })}
    </MenuTriggerContext.Provider>
  );
}
